import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Autocomplete, TextField } from '@mui/material';
import './CameraCaptureUploader.scss';

const CameraCaptureUploader: React.FC = () => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [imageName, setImageName] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingSuccess, setUploadingSuccess] = useState(false);

  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [categoryError, setCategoryError] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [keywordList, setKeywordList] = useState<string[]>([]);
  const [isConfidential, setIsConfidential] = useState(true);

const [desktopStream, setDesktopStream] = useState<MediaStream | null>(null);
const [noCameraFound, setNoCameraFound] = useState(false);
const videoRef = React.useRef<HTMLVideoElement>(null);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.post(`/treatment_category`);
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCapturedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageName(e.target.value);
  };

  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeywords(e.target.value);
  };

  const handleKeywordAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.keyCode === 188) {
      e.preventDefault();
      const trimmed = keywords.trim().replace(/,$/, '');
      if (trimmed && !keywordList.includes(trimmed)) {
        setKeywordList([...keywordList, trimmed]);
      }
      setKeywords('');
    }
  };


const resizeAndCompressImage = (file, width, height, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const newFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(newFile);
            } else {
              reject(new Error("Image compression failed"));
            }
          },
          "image/jpeg",
          quality
        );
      };

      img.src = e.target.result;
    };

    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

  const handleImageUpload = async () => {
    const token = axios.defaults.headers.common.Authorization;

    if (!capturedFile || !imageName) {
      toast.error("Please capture a photo and enter a name.");
      return;
    }

    // if (selectedCategories.length === 0) {
    //   setCategoryError(true);
    //   toast.error("Please select at least one category.");
    //   return;
    // }

    setLoading(true);
    setUploadingSuccess(true);
    setIsConfidential(true);

    try {
       const resizedFile = await resizeAndCompressImage(capturedFile, 400, 400, 0.7); 
        const formData = new FormData();
          formData.append("file", resizedFile);
          formData.append("user_file_name", imageName);
          formData.append("keywords", keywordList.join(','));
          formData.append("is_confidential", isConfidential ? "true" : "false");

          selectedCategories.forEach((category) => {
            formData.append("category_ids", category.id.toString());
          });
        await axios.post("consent_clause/upload_user_media_to_s3", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token,
          },
        });
      

      toast.success("Content added to the Image gallery");

      setTimeout(() => {
        setImagePreview(null);
        setCapturedFile(null);
        setImageName('');
        setSelectedCategories([]);
        setKeywordList([]);
        setUploadingSuccess(false);
      }, 1500);
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading image.");
      setUploadingSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  useEffect(() => {
  if (!isMobile) {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        setDesktopStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch((err) => {
        console.error("No webcam found or permission denied:", err);
        setNoCameraFound(true);
      });
  }
}, [isMobile]);

useEffect(() => {
  return () => {
    if (desktopStream) {
      desktopStream.getTracks().forEach((track) => track.stop());
    }
  };
}, [desktopStream]);

  return (
    <div className="camera-uploader">
      <h2>Medicube - Image Uploader</h2>

      {loading && (
        <div className="uploading-overlay">
          <div className="spinner"></div>
          <div className="message">Uploading.....</div>
        </div>
      )}


      {!isMobile && (
  <div className="desktop-webcam-container">
    {noCameraFound ? (
      <p>No camera found on this device.</p>
    ) : (
      <div style={{ marginBottom: "20px" }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{ width: "100%", maxWidth: "400px", borderRadius: "8px" }}
        />
        <button
          onClick={() => {
            const canvas = document.createElement("canvas");
            const video = videoRef.current;
            if (video) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              const ctx = canvas.getContext("2d");
              if (ctx) {
                ctx.drawImage(video, 0, 0);
                canvas.toBlob((blob) => {
                  if (blob) {
                    const file = new File([blob], "captured.jpg", {
                      type: "image/jpeg",
                    });
                    setCapturedFile(file);
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setImagePreview(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                }, "image/jpeg");
              }
            }
          }}
          style={{ marginTop: "10px" }}
        >
          Capture Photo
        </button>
      </div>
    )}
  </div>
)}


      <label className={`upload-button ${loading ? 'disabled' : ''}`}>
        📷 Capture Photo
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleImageChange}
          disabled={loading}
        />
      </label>

      {imagePreview && (
        <>
          <img src={imagePreview} alt="Preview" className="preview-image" />
        <div
        style={{
            marginTop: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "90%",
        }}
        >
        <label style={{ marginBottom: "10px", fontSize: "14px" }}>
            <input
            type="checkbox"
            checked={isConfidential}
            onChange={(e) => setIsConfidential(e.target.checked)}
            />
            {" "}
            Mark this image as confidential
        </label>
        </div>
          <input
            type="text"
            value={imageName}
            onChange={handleNameChange}
            placeholder="Enter image name"
            className="input-field"
            disabled={loading}
          />

          <Autocomplete
            multiple
            id="categories-autocomplete"
            options={categories}
            getOptionLabel={(option) => option.category_name}
            value={selectedCategories}
            onChange={(event, newValue) => {
              setSelectedCategories(newValue);
              setCategoryError(false);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select categories"
                placeholder="Start typing to search..."
                error={categoryError}
                helperText={
                  categoryError ? 'Please select at least one category' : ''
                }
              />
            )}
            className="autocomplete"
            disabled={loading}
          />

     
          <input
            type="text"
            placeholder="Enter keywords and add comas to separate"
            value={keywords}
            onChange={handleKeywordChange}
            onKeyDown={handleKeywordAdd}
            style={{
              width: '97%',
              padding: '14px',
              marginBottom: '10px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              backgroundColor: '#f9f9f9',
              transition: 'all 0.3s ease-in-out',
            }}
            onFocus={(e) => (e.target.style.borderColor = '#0056b3')}
            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
          />


          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
            {keywordList.map((kw, idx) => (
              <div
                key={idx}
                style={{
                  backgroundColor: '#e0f0ff',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {kw}
                <span
                  onClick={() =>
                    setKeywordList(keywordList.filter((_, i) => i !== idx))
                  }
                  style={{
                    marginLeft: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    color: '#333',
                  }}
                >
                  ×
                </span>
              </div>
            ))}
          </div>

          {!uploadingSuccess && (
            <button
              onClick={handleImageUpload}
              disabled={loading}
              className="save-button"
            >
              Save Image
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CameraCaptureUploader;
